import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useDemoMode } from '../../context/DemoModeContext.js';
import { api } from '../../lib/api.js';
import { BackLink } from '../../components/ui/BackLink.js';
import type {
  SchemeMechanic,
  ArchetypeKey,
  ExternalVerification,
  ExtractionClassification,
} from '@infoscend-claims/shared';
import { ARCHETYPE_DEFINITIONS } from '../../lib/schemeArchetypes.js';
import {
  SchemeIcon,
  MadLibsEditor,
  ExternalVerificationPanel,
  TaxBlock,
  FundAllocationBlock,
} from '../../components/scheme/SchemeEditor.js';
import { ExtractionClassificationCard } from '../../components/admin/ExtractionClassificationCard.js';

// ── Archetype Picker ─────────────────────────────────────────────────────────

function ArchetypePicker({ selected, onSelect }: { selected: ArchetypeKey | null; onSelect: (key: ArchetypeKey) => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
        {t('admin.schemes.archetypes.pickTitle', { defaultValue: '1. Choose a Scheme Pattern' })}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ARCHETYPE_DEFINITIONS.map((arch) => (
          <button
            key={arch.key}
            onClick={() => onSelect(arch.key)}
            className={`text-left p-3 border rounded transition-all ${
              selected === arch.key
                ? 'border-copper bg-copper/5 shadow-sm'
                : 'border-hairline hover:border-copper/40 bg-white'
            }`}
          >
            <div className={`mb-1.5 ${selected === arch.key ? 'text-copper' : 'text-slate-400'}`}>
              <SchemeIcon name={arch.lucideIcon} size={18} />
            </div>
            <div className="text-[11px] font-semibold italic font-serif text-ink leading-tight">
              {t(`admin.schemes.archetypes.${arch.key}.name`, { defaultValue: arch.key.replace(/_/g, ' ') })}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">
              {t(`admin.schemes.archetypes.${arch.key}.description`, { defaultValue: '' })}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function deepClone<T>(x: T): T {
  return JSON.parse(JSON.stringify(x)) as T;
}

export function SchemeFormPage() {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  // Core metadata
  const [schemeCode, setSchemeCode] = useState('');
  const [schemeType, setSchemeType] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [isDemoSeed, setIsDemoSeed] = useState(false);

  // Mechanic (the new structured shape)
  const [mechanic, setMechanic] = useState<SchemeMechanic>(ARCHETYPE_DEFINITIONS[0]!.template);
  const [selectedArchetype, setSelectedArchetype] = useState<ArchetypeKey | null>(null);

  // Tax
  const [taxTreatment, setTaxTreatment] = useState('NET_DEDUCT');
  const [pphCategory, setPphCategory] = useState('PPH_23_SERVICE');
  const [withholdingPercent, setWithholdingPercent] = useState('2.00');

  // AI classification metadata (populated from extractionMetadata on load)
  const [classification, setClassification] = useState<ExtractionClassification | null>(null);

  // Fund
  const [glAccountCode, setGlAccountCode] = useState('');
  const [sapWbsElement, setSapWbsElement] = useState('');
  const [budgetCapAmount, setBudgetCapAmount] = useState('');

  // External verifications (extracted from mechanic.constraints)
  const [externalVerifications, setExternalVerifications] = useState<ExternalVerification[]>([]);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Close popover on outside click
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const addRuleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get<Record<string, unknown>>(`/admin/schemes/${id}`)
      .then((data) => {
        setSchemeCode(String(data['schemeCode'] ?? ''));
        setSchemeType(String(data['schemeType'] ?? ''));
        setName(String(data['name'] ?? ''));
        setDescription(String(data['description'] ?? ''));
        setValidFrom(String(data['validFrom'] ?? '').slice(0, 10));
        setValidTo(String(data['validTo'] ?? '').slice(0, 10));
        setStatus((data['status'] as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE');
        setIsDemoSeed(Boolean(data['isDemoSeed']));

        const raw = (data['mechanic'] as SchemeMechanic | null);
        if (raw && raw.payout) {
          setMechanic({
            ...raw,
            eligibility: {
              logicalOperator: raw.eligibility?.logicalOperator ?? 'AND',
              conditions: raw.eligibility?.conditions ?? [],
            },
            constraints: {
              stackability: raw.constraints?.stackability ?? { rule: 'FULLY_STACKABLE' },
              externalVerifications: raw.constraints?.externalVerifications ?? [],
              targetEntities: raw.constraints?.targetEntities ?? {},
              ...(raw.constraints?.coolingOffDays != null ? { coolingOffDays: raw.constraints.coolingOffDays } : {}),
              ...(raw.constraints?.preApprovalThresholdAmount != null ? { preApprovalThresholdAmount: raw.constraints.preApprovalThresholdAmount } : {}),
            },
            fundAllocation: raw.fundAllocation ?? { glAccountCode: null, budgetCap: null },
          });
        }

        setTaxTreatment(String(data['taxTreatment'] ?? 'NET_DEDUCT'));
        setPphCategory(String(data['pphCategory'] ?? 'PPH_23_SERVICE'));
        setWithholdingPercent(data['taxWithholdingPercent'] != null ? String(Number(data['taxWithholdingPercent']) * 100) : '2.00');
        setGlAccountCode(String(data['glAccountCode'] ?? ''));
        setSapWbsElement(String(data['sapWbsElement'] ?? ''));
        setBudgetCapAmount(data['budgetCapAmount'] != null ? String(data['budgetCapAmount']) : '');

        const evs = data['externalVerifications'] as ExternalVerification[] | null;
        if (evs && Array.isArray(evs)) setExternalVerifications(evs);

        const em = data['extractionMetadata'] as { classification?: ExtractionClassification } | null;
        if (em?.classification) setClassification(em.classification);

        setLoading(false);
      })
      .catch((e: Error) => { setError(e.message); setLoading(false); });
  }, [id, isEdit]);

  // Sync externalVerifications into mechanic.constraints
  useEffect(() => {
    setMechanic((m) => ({
      ...m,
      constraints: { ...m.constraints, externalVerifications },
    }));
  }, [externalVerifications]);

  if (persona.role !== 'brand_admin') {
    return <div className="max-w-2xl mx-auto py-8 text-slate-500 text-sm">{t('errors.forbidden')}</div>;
  }

  if (loading) {
    return <div className="max-w-3xl mx-auto py-8 text-slate-400 text-sm">{t('common.loading')}</div>;
  }

  function handleArchetypeSelect(key: ArchetypeKey) {
    const archDef = ARCHETYPE_DEFINITIONS.find((a) => a.key === key);
    if (!archDef) return;
    setSelectedArchetype(key);
    const tmpl = deepClone(archDef.template);
    setMechanic(tmpl);
    setExternalVerifications(tmpl.constraints.externalVerifications ?? []);
    if (archDef.schemeTypes[0]) setSchemeType(archDef.schemeTypes[0]);
    if (tmpl.fundAllocation.glAccountCode) setGlAccountCode(tmpl.fundAllocation.glAccountCode);
    if (tmpl.fundAllocation.budgetCap) setBudgetCapAmount(String(tmpl.fundAllocation.budgetCap.amount));
  }

  function handleTaxField(field: string, val: string) {
    if (field === 'taxTreatment') setTaxTreatment(val);
    else if (field === 'pphCategory') setPphCategory(val);
    else if (field === 'withholdingPercent') setWithholdingPercent(val);
  }

  function handleFundField(field: string, val: string) {
    if (field === 'glAccountCode') setGlAccountCode(val);
    else if (field === 'sapWbsElement') setSapWbsElement(val);
    else if (field === 'budgetCapAmount') setBudgetCapAmount(val);
  }

  const eligibilityHasConditions = (mechanic.eligibility?.conditions ?? []).length > 0;
  const payoutValid = mechanic.payout.type !== 'FORMULA' || (mechanic.payout as { expression?: string }).expression;
  const formValid = !isDemoSeed && schemeCode && name && validFrom && validTo && eligibilityHasConditions && payoutValid;

  async function handleSave(saveAsDraft = false) {
    setError(null);
    setSuccess(null);
    setSubmitting(true);

    const mechanicWithFund: SchemeMechanic = {
      ...mechanic,
      fundAllocation: {
        glAccountCode: glAccountCode || null,
        budgetCap: budgetCapAmount ? { amount: Number(budgetCapAmount), currency: 'IDR' as const } : null,
        ...(sapWbsElement ? { sapWbsElement } : {}),
      },
    };

    const body = {
      schemeCode,
      schemeType,
      name,
      description,
      mechanic: mechanicWithFund,
      isoCurrencyCode: 'IDR',
      validFrom,
      validTo,
      budgetCap: budgetCapAmount ? Number(budgetCapAmount) : null,
      budgetCapAmount: budgetCapAmount ? Number(budgetCapAmount) : null,
      glAccountCode: glAccountCode || null,
      sapWbsElement: sapWbsElement || null,
      taxTreatment,
      pphCategory,
      taxWithholdingPercent: Number(withholdingPercent) / 100,
      externalVerifications: externalVerifications.length > 0 ? externalVerifications : null,
      status: saveAsDraft ? 'DRAFT' : status,
      eligibilityFilter: {},
    };

    try {
      if (isEdit) {
        await api.patch<unknown>(`/admin/schemes/${id}`, body);
        setSuccess(t('admin.schemes.successEdit'));
      } else {
        await api.post<unknown>('/admin/schemes', body);
        setSuccess(saveAsDraft ? t('admin.schemes.successDraft', { defaultValue: 'Draft saved' }) : t('admin.schemes.successCreate'));
        setTimeout(() => navigate('/admin/schemes'), 1200);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('schemeCodeExists')) setError(t('admin.schemes.errorSchemeCodeExists'));
      else setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const readOnly = isDemoSeed;

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-16">
      <div>
        <BackLink to="/admin/schemes" labelKey="admin.schemes.title" />
        <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-2 mt-4">{t('admin.title')}</span>
        <h1 className="font-serif italic text-3xl text-ink">
          {isEdit ? t('admin.schemes.editTitle') : t('admin.schemes.newTitle')}
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {t('admin.schemes.newSubtitle', { defaultValue: 'Pick a pattern, fill in the details, save. No JSON ever visible.' })}
        </p>
      </div>

      {isDemoSeed && (
        <div className="border border-amber-200 bg-amber-50 rounded px-4 py-3 text-sm text-amber-800">{t('admin.schemes.demoReadOnly')}</div>
      )}
      {error && <div className="border border-danger/30 bg-danger/5 rounded px-4 py-3 text-sm text-danger">{error}</div>}
      {success && <div className="border border-success/30 bg-success/5 rounded px-4 py-3 text-sm text-success">{success}</div>}

      {/* Section A: Core metadata */}
      <div className="bg-white border border-hairline rounded p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.schemeCode')}</label>
            <input disabled={readOnly} type="text" className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper font-mono"
              value={schemeCode} onChange={(e) => setSchemeCode(e.target.value)} placeholder="VIS-2026-JKT-01" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.status')}</label>
            <select disabled={readOnly} className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
              value={status} onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'INACTIVE')}>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.name')}</label>
          <input disabled={readOnly} type="text" className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
            value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.description')}</label>
          <textarea disabled={readOnly} rows={2} className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.validFrom')}</label>
            <input disabled={readOnly} type="date" className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
              value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.validTo')}</label>
            <input disabled={readOnly} type="date" className="w-full border border-hairline rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-copper"
              value={validTo} onChange={(e) => setValidTo(e.target.value)} required />
          </div>
        </div>
      </div>

      {/* Section B: Archetype picker */}
      {!isEdit && (
        <div className="bg-white border border-hairline rounded p-5">
          <ArchetypePicker selected={selectedArchetype} onSelect={handleArchetypeSelect} />
        </div>
      )}

      {/* AI classification card — shows on reload if scheme was AI-extracted */}
      {classification && <ExtractionClassificationCard classification={classification} />}

      {/* Section C: Mad-Libs editor */}
      <div className="bg-white border border-hairline rounded p-5">
        <MadLibsEditor mechanic={mechanic} onChange={setMechanic} readOnly={readOnly} />

        {/* Add Rule / External Verification */}
        {!readOnly && (
          <div className="mt-5 pt-4 border-t border-hairline" ref={addRuleRef}>
            <div className="relative inline-block">
              <button
                onClick={() => setAddRuleOpen((o) => !o)}
                className="text-xs border border-hairline rounded px-3 py-1.5 text-slate-600 hover:border-copper hover:text-copper transition-colors flex items-center gap-1"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {t('admin.schemes.addRule.button', { defaultValue: 'Add Rule / Exception' })}
              </button>
              {addRuleOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-hairline rounded shadow-md z-10 min-w-52 py-1">
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-bone"
                    onClick={() => { setMechanic((m) => ({ ...m, eligibility: { ...(m.eligibility ?? { logicalOperator: 'AND' as const }), conditions: [...(m.eligibility?.conditions ?? []), { type: 'CUSTOM_PREDICATE', description: '' }] } })); setAddRuleOpen(false); }}>
                    {t('admin.schemes.addRule.menu.eligibilityCondition', { defaultValue: '+ Eligibility condition' })}
                  </button>
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-bone"
                    onClick={() => { setExternalVerifications((v) => [...v, { constraintName: '', description: '', resolutionMethod: 'MANUAL_APPROVAL', routingRole: 'FINANCE_MANAGER' }]); setAddRuleOpen(false); }}>
                    {t('admin.schemes.addRule.menu.externalVerification', { defaultValue: '+ External verification' })}
                  </button>
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-bone"
                    onClick={() => { setMechanic((m) => ({ ...m, constraints: { ...m.constraints, coolingOffDays: 30 } })); setAddRuleOpen(false); }}>
                    {t('admin.schemes.addRule.menu.coolingOff', { defaultValue: '+ Set cooling-off period' })}
                  </button>
                  <button className="w-full text-left px-3 py-2 text-xs hover:bg-bone"
                    onClick={() => { setMechanic((m) => ({ ...m, constraints: { ...m.constraints, preApprovalThresholdAmount: 5000000 } })); setAddRuleOpen(false); }}>
                    {t('admin.schemes.addRule.menu.preApproval', { defaultValue: '+ Pre-approval threshold' })}
                  </button>
                </div>
              )}
            </div>

            {/* Additional rule pills */}
            <div className="mt-3 space-y-1.5">
              {mechanic.constraints.coolingOffDays != null && (
                <div className="text-xs text-slate-600 flex items-center gap-2 bg-bone rounded px-3 py-1.5">
                  ⚙ Cooling-off: distributor can claim every
                  <input type="number" className="border border-hairline rounded px-2 py-0.5 w-14 text-xs focus:outline-none focus:ring-1 focus:ring-copper"
                    value={mechanic.constraints.coolingOffDays}
                    onChange={(e) => setMechanic((m) => ({ ...m, constraints: { ...m.constraints, coolingOffDays: Number(e.target.value) } }))} />
                  days max.
                  <button className="ml-auto text-slate-400 hover:text-danger"
                    onClick={() => setMechanic((m) => { const c = { ...m.constraints }; delete c.coolingOffDays; return { ...m, constraints: c }; })}>×</button>
                </div>
              )}
              {mechanic.constraints.preApprovalThresholdAmount != null && (
                <div className="text-xs text-slate-600 flex items-center gap-2 bg-bone rounded px-3 py-1.5">
                  ⚙ Pre-approval required for claims above Rp
                  <input type="number" className="border border-hairline rounded px-2 py-0.5 w-28 text-xs focus:outline-none focus:ring-1 focus:ring-copper"
                    value={mechanic.constraints.preApprovalThresholdAmount}
                    onChange={(e) => setMechanic((m) => ({ ...m, constraints: { ...m.constraints, preApprovalThresholdAmount: Number(e.target.value) } }))} />.
                  <button className="ml-auto text-slate-400 hover:text-danger"
                    onClick={() => setMechanic((m) => { const c = { ...m.constraints }; delete c.preApprovalThresholdAmount; return { ...m, constraints: c }; })}>×</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* External verifications panel */}
      {(externalVerifications.length > 0 || !readOnly) && (
        <div className="bg-white border border-hairline rounded p-5">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
            {t('admin.schemes.externalVerifications.title', { defaultValue: 'External Verifications' })}
          </h3>
          <ExternalVerificationPanel
            verifications={externalVerifications}
            onChange={setExternalVerifications}
            readOnly={readOnly}
          />
        </div>
      )}

      {/* Tax block */}
      <div className="bg-white border border-hairline rounded p-5">
        <TaxBlock taxTreatment={taxTreatment} pphCategory={pphCategory} withholdingPercent={withholdingPercent} onChange={handleTaxField} readOnly={readOnly} />
      </div>

      {/* Fund allocation block */}
      <div className="bg-white border border-hairline rounded p-5">
        <FundAllocationBlock glAccountCode={glAccountCode} sapWbsElement={sapWbsElement} budgetCapAmount={budgetCapAmount} onChange={handleFundField} readOnly={readOnly} />
      </div>

      {/* Action bar */}
      {!readOnly && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-hairline px-6 py-3 flex items-center gap-3 z-20">
          <button
            disabled={submitting || !formValid}
            onClick={() => handleSave(false)}
            className="px-5 py-2 bg-copper text-white text-sm font-semibold rounded hover:bg-copper-deep disabled:opacity-50 transition-colors"
          >
            {submitting ? t('admin.schemes.saving') : t('admin.schemes.save')}
          </button>
          <button
            disabled={submitting}
            onClick={() => handleSave(true)}
            className="px-4 py-2 text-sm text-slate-600 border border-hairline rounded hover:border-slate-300 transition-colors"
          >
            {t('admin.schemes.saveAsDraft', { defaultValue: 'Save as Draft' })}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/schemes')}
            className="px-4 py-2 text-sm text-slate-400 hover:text-ink transition-colors"
          >
            {t('admin.schemes.cancel')}
          </button>
          {!eligibilityHasConditions && (
            <span className="text-xs text-danger ml-2">
              ⚠ {t('admin.schemes.madLibs.eligibilityRequired', { defaultValue: 'Add at least one eligibility condition' })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
